import { Link, Outlet, createRootRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ErrorState } from '../components/ErrorState';
import { Icon } from '../components/Icon';
import { Sidebar } from '../components/Sidebar';
import { UserMenu } from '../components/UserMenu';
import { useAuthStore } from '../lib/auth-store';

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundRoot,
  errorComponent: ErrorRoot,
});

function RootComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentCompanyId, availableCompanies, logout, switchCompany } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // v1.26 bug fix: 防止 useEffect 2 在 fetchMe 还在跑 (async) 时就因 user null
  // 立即跳 /login. 必须等 fetchMe 跑完 (成功/失败) 后再决定是否跳 login.
  const [authChecked, setAuthChecked] = useState(false);

  // 跑 fetchMe, 完成后标记 authChecked = true (无论成功失败).
  // 用 getState() 取稳定引用避免 zustand 函数引用变化触发 useEffect 反复跑.
  useEffect(() => {
    if (location.pathname !== '/login') {
      useAuthStore
        .getState()
        .fetchMe()
        .finally(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, [location.pathname]);

  // 只有 authChecked 后 (=fetchMe 跑完) 才判断是否要跳 /login.
  // 修复: 之前立即跳导致 "fetchMe 还没完成就跳 /login" 死循环.
  useEffect(() => {
    if (authChecked && !user && location.pathname !== '/login') {
      navigate({ to: '/login' });
    }
  }, [user, authChecked, location.pathname, navigate]);

  // 路由变化时关闭移动端 sidebar
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在 pathname 变时触发
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (location.pathname === '/login') {
    return (
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <span className="text-ink-3 text-sm animate-pulse">加载中…</span>
      </div>
    );
  }

  const currentCompany = availableCompanies.find((c) => c.id === currentCompanyId) ?? null;

  return (
    <div className="min-h-screen bg-bg flex">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentCompany={currentCompany}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar — 移动端 hamburger + 桌面 minimal */}
        <header className="sticky top-0 z-30 bg-surface/85 backdrop-blur border-b border-line">
          <div className="px-4 md:px-6 h-14 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center text-ink-2 hover:bg-surface-2 rounded-md"
                aria-label="打开菜单"
              >
                <Icon name="menu" size="md" aria-hidden />
              </button>
              <Link to="/" className="flex items-center gap-2 md:hidden">
                <span className="w-6 h-6 rounded bg-accent text-white flex items-center justify-center text-xs font-bold">
                  贸
                </span>
                <span className="font-semibold text-sm">Trade OS</span>
              </Link>
              <div className="hidden md:block text-sm text-ink-3">
                {currentCompany && (
                  <span>
                    {currentCompany.flag} {currentCompany.name} · {currentCompany.code}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <UserMenu
                user={user}
                availableCompanies={availableCompanies}
                currentCompanyId={currentCompanyId}
                onSwitchCompany={async (id) => {
                  await switchCompany(id);
                }}
                onLogout={async () => {
                  await logout();
                }}
              />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </div>
  );
}

/** v1.9 P0: 全局 404 兜底 — TanStack Router 路由匹配不到时触发 */
function NotFoundRoot() {
  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* 简单 top bar 维持品牌感 */}
      <header className="px-4 md:px-6 h-14 flex items-center border-b border-line bg-surface/85 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-7 h-7 rounded bg-accent text-white flex items-center justify-center text-xs font-bold">
            贸
          </span>
          <span className="font-semibold text-sm">Trade OS</span>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center">
        <ErrorState kind="not-found" />
      </div>
    </div>
  );
}

/** v1.9 P0: 全局 router 错误兜底 */
function ErrorRoot({ error }: { error: unknown }) {
  const err = error instanceof Error ? error : new Error(String(error));
  // 网络错误简单判断: 包含 fetch / network / failed to fetch
  const msg = (err.message ?? '').toLowerCase();
  const isNetwork =
    msg.includes('fetch') || msg.includes('network') || msg.includes('failed to load');
  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <header className="px-4 md:px-6 h-14 flex items-center border-b border-line bg-surface/85 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-7 h-7 rounded bg-accent text-white flex items-center justify-center text-xs font-bold">
            贸
          </span>
          <span className="font-semibold text-sm">Trade OS</span>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center">
        <ErrorState
          kind={isNetwork ? 'network' : 'server'}
          error={isNetwork ? null : err}
          onRetry={() => {
            if (typeof window !== 'undefined') window.location.reload();
          }}
        />
      </div>
    </div>
  );
}
